import * as m from "@paraglide/messages"
import { PlaydataPdfViewer } from "@playdata/PlaydataPdfViewer"
import { PlaydataButtonAnchor, PlaydataButtonLink, PlaydataSection } from "@playdata/PlaydataPrimitives"
import { usePlaydataSampleReportUrl } from "@playdata/playdata-settings-context"
import type { SupportedLocale } from "@site-types/content"
import { createFileRoute } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"

export const Route = createFileRoute("/$locale/playdata/sample")({
  head: ({ params }) => {
    const locale = params.locale as SupportedLocale
    return playdataRouteHead({
      description: m.playdata_sample_meta_description({}, { locale }),
      locale,
      path: "/playdata/sample",
      title: m.playdata_sample_meta_title({}, { locale }),
    })
  },
  component: RouteComponent,
})

/*
 * The live page is thin: title, one line, the PDF button, a note, and contact.
 * The reader is the one addition — the report is the point of the page, and
 * asking for a download before you can see anything is a worse first look.
 * The button stays exactly where it was, because on a phone it is the only
 * way to read the thing.
 */
function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const locale = raw as SupportedLocale
  const sampleReportUrl = usePlaydataSampleReportUrl(locale)
  return (
    <PlaydataSection>
      <div className="flex flex-col items-center gap-[2.4rem] text-center">
        <h1 className="text-[2.8rem] md:text-[3.4rem]">
          <strong>{m.playdata_sample_heading({}, { locale })}</strong>
        </h1>
        <p className="text-[1.6rem]">{m.playdata_sample_lead({}, { locale })}</p>
        <PlaydataButtonAnchor
          href={sampleReportUrl}
          rel="noopener"
          target="_blank"
        >
          {m.playdata_sample_cta({}, { locale })}
        </PlaydataButtonAnchor>
        <p className="text-[1.3rem]">※{m.playdata_sample_note({}, { locale })}</p>
        <PlaydataPdfViewer
          locale={locale}
          title={m.playdata_sample_heading({}, { locale })}
          url={sampleReportUrl}
        />
        <PlaydataButtonLink
          params={{ locale }}
          to="/$locale/contact"
          variant="outline"
        >
          {m.playdata_common_contactCta({}, { locale })}
        </PlaydataButtonLink>
      </div>
    </PlaydataSection>
  )
}
